import posthog from 'posthog-js'

import type { GetMeResponse } from '@snootsvet/shared/portal'

/**
 * Whether `initPostHog` actually booted the SDK. Without a key we never
 * call `init`, and every subsequent SDK call would warn to the console -
 * which is the normal state for local dev, vitest, and Storybook.
 */
let enabled = false

/** Initialize PostHog product analytics and session replay. */
export function initPostHog() {
  const key = import.meta.env.VITE_POSTHOG_KEY

  if (!key) {
    console.warn('PostHog key not provided — PostHog will not be initialized.')
    return
  }

  posthog.init(key, {
    // EU cloud by default; override for a US-hosted project or a proxy.
    api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://eu.i.posthog.com',
    // Pin the behaviour set so an SDK upgrade can't silently change
    // what we capture. Bump deliberately, not incidentally.
    defaults: '2026-06-25',
    // The portal is a SPA - `BrowserRouter` moves between pages via the
    // History API, which never triggers a page load. Without this we'd
    // only ever record the first pageview of a session.
    capture_pageview: 'history_change',
    // Replay is on to see where members get stuck, but every input is
    // masked: the portal collects email, phone, and address. Stripe's
    // card fields are cross-origin iframes, so they're never recorded
    // regardless.
    session_recording: { maskAllInputs: true },
  })

  enabled = true
}

/** Points PostHog at the member driving the current session. */
export function syncPostHogMember(
  member: GetMeResponse['member'] | undefined,
  impersonatedBy: string | null | undefined
) {
  if (!enabled) return
  if (impersonatedBy) {
    posthog.opt_out_capturing()
    return
  }
  if (!member) return

  // An impersonated session that ended without unmounting (tab closed
  // mid-session) leaves the opt-out persisted in local storage. Clear
  // it here, or that browser stays silently untracked for every member
  // who signs in afterwards.
  if (posthog.has_opted_out_capturing()) {
    posthog.opt_in_capturing({ captureEventName: false })
  }

  // Super property: every event (pageviews included) carries `region`, so MAU insights can
  // break down by it without joining through the person.
  posthog.register({ region: member.region })
  posthog.identify(member.id, { email: member.email, region: member.region })
}

/**
 * Captures a product event. Attributed to the identified member on portal
 * pages and anonymous on the public booking-link pages, which never call
 * `identify`. Dropped during staff impersonation.
 */
export function captureEvent(event: string, properties?: Record<string, unknown>) {
  if (!enabled) return
  posthog.capture(event, properties)
}

/**
 * Clears the identified member and starts a fresh session. Called when
 * the portal route tree unmounts, i.e. whenever a session ends - see
 * `AnalyticsUserSync` in `app.tsx`.
 */
export function resetPostHog() {
  if (!enabled) return
  posthog.reset()
}
